import * as R from "ramda";
import React from "react";
import {
  List,
  Datagrid,
  ArrayField,
  ChipField,
  DateField,
  FunctionField,
  NumberField,
  TextField,
  SingleFieldList,
  ShowButton,
  SimpleShowLayout,
  BooleanField,
} from "react-admin";
import {
  DoctorReferenceField,
  PatientReferenceField,
} from "../../misc/customFields";
import { completedSessionExporter } from "../../misc/exports.js";
import { SessionFilter } from "../../misc/filters";
import DevicesAside from "./DevicesAside";
import SessionShow from "./utils";

import withStyles from "@material-ui/core/styles/withStyles";

const listStyles = {
  card: {
    "& div": {
      justifyContent: "flex-start",
    },
  },
};

export const PowerdotCompletedSessionList = withStyles(listStyles)(
  ({ classes, ...props }) => (
    <List
      classes={classes}
      {...props}
      exporter={completedSessionExporter}
      filters={<SessionFilter />}
      bulkActionButtons={null}
    >
      <Datagrid {...props}>
        <ShowButton />
        <TextField source="id" />
        <DoctorReferenceField sortBy="doctor__first_name" label="Clinician" />
        <PatientReferenceField
          label="Patient"
          source="patientInfo.id"
          allowEmpty
          sortBy="patientInfo"
        />
        <TextField
          source="platformInfo.os"
          label="OS"
          sortBy="platformInfo__os"
        />
        <TextField
          source="platformInfo.phoneModel"
          label="Model"
          sortBy="platformInfo__phoneModel"
        />
        <FunctionField
          label="Completeness"
          render={(record) =>
            `${R.pathOr(
              "None",
              ["completionSummary", "completionPercent"],
              record
            )} %`
          }
        />
        <TextField source="mode" sortable={false} />
        <NumberField
          source="deviceNumber"
          label="Devices Number"
          sortable={false}
        />
        <ArrayField source="devices" sortable={false} label="Serials">
          <SingleFieldList>
            <ChipField source="serialNumber" />
          </SingleFieldList>
        </ArrayField>
        <TextField source="programName" label="Program" sortable={false} />
        <TextField
          source="muscleGroupName"
          label="Muscle Group"
          sortable={false}
        />
        <TextField source="type" sortable={false} />
        <BooleanField source="is_remote" sortable={false} label="Remote" />
        <TextField
          source="completionSummary.intensitySummary.max"
          label="Max Intensity"
          sortable={false}
        />
        <TextField
          source="completionSummary.intensitySummary.longest"
          label="Longest Intensity"
          sortable={false}
        />
        <NumberField
          source="painScoreBefore"
          label="Pain Score Before"
          sortable={false}
        />
        <NumberField
          source="completionSummary.painScoreAfter"
          label="Pain Score After"
          sortable={false}
        />
        <NumberField
          source="completionSummary.totalPauseSeconds"
          label="Total Pause Seconds"
          sortBy="completion__totalPauseSeconds"
        />
        <DateField source="startDate" showTime />
        <DateField
          source="completionSummary.finishDate"
          showTime
          label="End Date"
        />
      </Datagrid>
    </List>
  )
);

const editStyles = {
  root: { alignItems: "flex-start" },
  inlineField: {
    display: "inline-block",
    width: "50%",
    whiteSpace: "nowrap",
  },
  inlineField3: {
    display: "inline-block",
    width: "33%",
  },
  inlineField1: {
    display: "inline-block",
  },
};

const CompletedSessionShow_ = ({ classes, ...props }) => {
  return (
    <SessionShow {...props} aside={<DevicesAside />} showTitle="Session Info">
      <SimpleShowLayout>
        <TextField source="id" className={classes.inlineField} />
        <DoctorReferenceField
          record={props.record}
          {...props}
          className={classes.inlineField}
          label="Clinician"
        />
        <PatientReferenceField
          label="Patient"
          source="patientInfo.id"
          allowEmpty
          className={classes.inlineField}
        />
        <TextField
          source="platformInfo.os"
          label="OS"
          className={classes.inlineField}
        />
        <TextField
          source="platformInfo.phoneModel"
          label="Model"
          className={classes.inlineField}
        />
        <TextField
          source="platformInfo.appVersion"
          label="App Version"
          className={classes.inlineField}
        />
        <FunctionField
          label="Completness"
          render={(record) =>
            `${R.pathOr(
              "None",
              ["completionSummary", "completionPercent"],
              record
            )} %`
          }
          className={classes.inlineField}
        />
        <TextField source="mode" className={classes.inlineField} />
        <NumberField
          source="deviceNumber"
          label="Devices Number"
          className={classes.inlineField}
        />
        <TextField
          source="programType"
          label="Program Type"
          className={classes.inlineField}
        />
        <TextField
          source="programName"
          label="Program"
          className={classes.inlineField}
        />
        <TextField
          source="muscleGroupName"
          label="Muscle Group"
          className={classes.inlineField}
        />
        <TextField source="type" className={classes.inlineField} />
        <TextField
          source="completionSummary.intensitySummary.max"
          label="Max Intensity"
          className={classes.inlineField}
        />
        <TextField
          source="completionSummary.intensitySummary.longest"
          label="Longest Intensity"
          className={classes.inlineField}
        />
        <NumberField
          source="painScoreBefore"
          label="Pain Score Before"
          className={classes.inlineField}
        />
        <NumberField
          source="completionSummary.painScoreAfter"
          label="Pain Score After"
          className={classes.inlineField}
        />
        <NumberField
          source="completionSummary.totalPauseSeconds"
          label="Total Pause Seconds"
          className={classes.inlineField}
        />
        <DateField
          source="startDate"
          showTime
          className={classes.inlineField}
        />
        <DateField
          source="completionSummary.finishDate"
          showTime
          label="End Date"
          className={classes.inlineField}
        />
        <TextField
          source="completionSummary.cancelReason"
          label="Cancel Reason"
          className={classes.inlineField}
        />
      </SimpleShowLayout>
    </SessionShow>
  );
};

export const PowerdotCompletedSessionShow = withStyles(editStyles)(
  CompletedSessionShow_
);
